import React from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { isAndroid, Popup } from '@helpers';
import TouchableElement from '@components/common/touchableElement';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import Calendar from '@components/common/Calendar';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageHeader } from '@components/machines/components/eventsChart/hooks/useManageHeader';

const RenderElement = ({ name, time, C, loaded, dateText, color, colorDot, showDot, setSelectedChartDate, selectedChartDate, testID }) => {
  const showCalendar = () => {
    Popup({
      Component: (p) => <Calendar {...{ ...p, C, setSelectedChartDate, selectedChartDate }} />,
      avoidingViewEnable: false,
    });
  };

  return (
    <TouchableElement onPress={showCalendar}>
      {({ boxStyle }) => (
        <View testID={testID} style={[boxStyle, { marginTop: scale(9), height: scale(81), width: '50%' }]}>
          <Text
            style={[
              {
                color,
              },
              font['Button 2'],
            ]}
          >
            {i18n.t(name)}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                opacity: loaded ? 1 : 0,
              }}
            >
              <Text
                style={[
                  {
                    color,
                  },
                  font['Large Title'],
                ]}
              >
                {Number(time.h)}
                <Text
                  style={[
                    font['Button 1'],
                    {
                      color,
                    },
                    isAndroid && { lineHeight: scale(36) },
                  ]}
                >
                  {i18n.t('h')}{' '}
                </Text>
              </Text>
              {Number(time.h) < 100 && (
                <Text
                  style={[
                    {
                      color,
                    },
                    font['Large Title'],
                  ]}
                >
                  {Number(time.m)}
                  <Text
                    style={[
                      {
                        color,
                      },
                      font['Button 1'],
                      isAndroid && { lineHeight: scale(36) },
                    ]}
                  >
                    {i18n.t('m')}
                  </Text>
                </Text>
              )}
            </View>
          </View>
          <View style={[{ flexDirection: 'row', alignItems: 'center', opacity: loaded ? 1 : 0 }]}>
            {showDot && (
              <View
                style={{
                  width: scale(10),
                  height: scale(10),
                  borderRadius: scale(10) / 2,
                  backgroundColor: colorDot || color,
                  marginRight: scale(3),
                }}
              />
            )}
            <Text
              style={[
                {
                  color,
                },
                font['Button 1'],
              ]}
            >
              {dateText}
            </Text>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

export default ({ C, triggerLine, showLine, dataPrev, data, setSelectedChartDate, selectedChartDate, isActive, chartTitle, loading = false }) => {
  const { loaded, time, timePrev, dateText, dateTextPrev, currentColor, prevColor, prevDotColor } = useManageHeader({
    C,
    data,
    dataPrev,
    isActive,
    chartTitle,
    loading,
  });

  if (showLine) {
    return (
      <View testID={TEST_IDS.chartHeader.container} style={{ width: '100%', flexDirection: 'row', height: scale(81), zIndex: 10000 }}>
        <RenderElement
          {...{
            name: chartTitle,
            time,
            C,
            loaded,
            dateText,
            color: currentColor,
            showDot: true,
            setSelectedChartDate,
            selectedChartDate,
            testID: TEST_IDS.chartHeader.currentElement,
          }}
        />
        <RenderElement
          {...{
            name: chartTitle,
            time: timePrev,
            C,
            loaded,
            dateText: dateTextPrev,
            color: prevColor,
            colorDot: prevDotColor,
            showDot: true,
            setSelectedChartDate,
            selectedChartDate,
            testID: TEST_IDS.chartHeader.prevElement,
          }}
        />
        <TouchableElement testID={TEST_IDS.chartHeader.toggleToBarButton} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }} onPress={triggerLine}>
          {({ boxStyle, pressHoverColor }) => {
            return (
              <View
                style={[
                  boxStyle,
                  {
                    height: scale(28),
                    width: scale(28),
                    position: 'absolute',
                    right: 0,
                    top: scale(10),
                    backgroundColor: C(pressHoverColor('Button/Light/Secondary')),
                    borderRadius: scale(28),
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                ]}
              >
                <Svg icon="barChart" color="Button/Light/Primary" width={scale(18)} height={scale(15)} />
              </View>
            );
          }}
        </TouchableElement>
      </View>
    );
  }

  return (
    <View testID={TEST_IDS.chartHeader.container} style={{ width: '100%', flexDirection: 'row', height: scale(81), zIndex: 10000 }}>
      <View style={{ width: '100%' }}>
        <RenderElement
          {...{
            name: chartTitle,
            time,
            C,
            loaded,
            dateText,
            color: currentColor,
            showDot: false,
            setSelectedChartDate,
            selectedChartDate,
            testID: TEST_IDS.chartHeader.currentElement,
          }}
        />
        <TouchableElement testID={TEST_IDS.chartHeader.toggleToLineButton} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }} onPress={triggerLine}>
          {({ boxStyle, pressHoverColor }) => {
            return (
              <View
                style={[
                  boxStyle,
                  {
                    height: scale(28),
                    width: scale(28),
                    position: 'absolute',
                    right: 0,
                    top: scale(10),
                    backgroundColor: C(pressHoverColor('Button/Light/Secondary')),
                    borderRadius: scale(28),
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                ]}
              >
                <Svg icon="lineChart" color="Button/Light/Primary" width={scale(18)} height={scale(15)} />
              </View>
            );
          }}
        </TouchableElement>
      </View>
    </View>
  );
};
